import { useEffect, useState } from "react";
import { AssetTimeseriesGroupBy } from "~/modules/asset_timeseries_v4/types";
import {
    AggregateFunctions,
    AggregateQueryInterval,
} from "~/modules/filters/types";
import { AggregateQueryControls, VisualizationMode } from "./types";
import {
    DEFAULT_GROUP_BY_CONTROLS,
    DEFAULT_AGGREGATE_FUNCTION_CONTROLS,
    DEFAULT_AGGREGATE_INTERVAL_CONTROLS,
} from "./consts";
import { TokenTimeseriesGroupBy } from "~/modules/token_timeseries/types";

// // // //

/**
 * Mmanages the state of AggregateQuery controls on the current visualization mode
 * @param {VisualizationMode} mode - The current visualization mode influencing the initial aggregate query controls configuration.
 * @returns {AggregateQueryControls} The current state of the 'groupBy' and 'aggregateFunction' controls, including their values and change handlers.
 */
export function useAggregateQueryControls(
    mode: VisualizationMode, // The current visualization mode dictating the initial configuration.
): AggregateQueryControls {
    // Extract query controls from the mode, falling back to defaults if not specified.
    const qc = mode.queryControls;
    const groupBy = qc && qc.groupBy ? qc.groupBy : DEFAULT_GROUP_BY_CONTROLS;
    const aggregateFunction =
        qc && qc.aggregateFunction
            ? qc.aggregateFunction
            : DEFAULT_AGGREGATE_FUNCTION_CONTROLS;

    const aggregateInterval =
        qc && qc.interval ? qc.interval : DEFAULT_AGGREGATE_INTERVAL_CONTROLS;

    // State hooks for managing control values.
    const [groupByValue, setGroupByValue] = useState(groupBy.value);
    const [aggregateFunctionValue, setAggregateFunctionValue] = useState(
        aggregateFunction.value,
    );
    const [intervalValue, setIntervalValue] = useState(aggregateInterval.value);

    // Effect hooks to update control values when the visualization mode changes.
    useEffect(() => {
        if (!groupBy.forceUpdate) return; // Skip update if not required.
        setGroupByValue(groupBy.value); // Update groupBy value to reflect current mode.
    }, [mode]);

    useEffect(() => {
        if (!aggregateFunction.forceUpdate) return; // Skip update if not required.
        setAggregateFunctionValue(aggregateFunction.value); // Update aggregateFunction value to reflect current mode.
    }, [mode]);

    useEffect(() => {
        if (!aggregateInterval.forceUpdate) return; // Skip update if not required.
        setIntervalValue(aggregateInterval.value); // Update aggregateFunction value to reflect current mode.
    }, [mode]);

    // Handlers for changing control values.
    const handleGroupByChange = (
        updatedVal: AssetTimeseriesGroupBy | TokenTimeseriesGroupBy,
    ) => {
        setGroupByValue(updatedVal); // Update groupBy state.
    };

    const handleAggregateFunctionChange = (updatedVal: AggregateFunctions) => {
        setAggregateFunctionValue(updatedVal); // Update aggregateFunction state.
    };

    const handleAggregateIntervalChange = (
        updatedVal: AggregateQueryInterval,
    ) => {
        setIntervalValue(updatedVal); // Update interval state.
    };

    // Return the stateful controls with their current values and change handlers.
    return {
        // NOTE - this ts-ignore statement is here because the groupBy value is a union type of TokenTimeseriesGroupBy and AssetTimeseriesGroupBy
        // We'll circle back to refining this pattern once we have some extra cycles
        // @ts-ignore
        groupBy: {
            ...groupBy,
            value: groupByValue,
            onChange: handleGroupByChange,
        },
        aggregateFunction: {
            ...aggregateFunction,
            value: aggregateFunctionValue,
            onChange: handleAggregateFunctionChange,
        },
        interval: {
            ...aggregateInterval,
            value: intervalValue,
            onChange: handleAggregateIntervalChange,
        },
    };
}
